import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Add, Clock, Edit2, InfoCircle, Trash } from 'iconsax-reactjs';
import {
  MARKER_ZONES,
  formatMinute,
  importanceMarks,
  type TimePoint,
  type TimeWindow,
} from '@pipyar/core';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { PairIcon } from '@/components/PairIcon';
import { PointForm, WindowForm } from './MarkerForm';
import { useI18n } from '@/lib/i18n';
import { GapIcon } from '@/components/GapIcon';
import { SoundSettings } from './SoundSettings';
import {
  useCreatePoint,
  useCreateWindow,
  useDeletePoint,
  useDeleteWindow,
  useSettingsInstruments,
  useTimePoints,
  useTimeWindows,
  useUpdateInstrument,
  useUpdatePoint,
  useUpdateWindow,
} from './api';

type Editing =
  | { kind: 'window'; value?: TimeWindow }
  | { kind: 'point'; value?: TimePoint }
  | null;

export function SettingsPage() {
  const { t } = useI18n();
  const [editing, setEditing] = useState<Editing>(null);

  const instrumentsQuery = useSettingsInstruments();
  const windows = useTimeWindows();
  const points = useTimePoints();

  const createWindow = useCreateWindow();
  const updateWindow = useUpdateWindow();
  const deleteWindow = useDeleteWindow();
  const createPoint = useCreatePoint();
  const updatePoint = useUpdatePoint();
  const deletePoint = useDeletePoint();
  const updateInstrument = useUpdateInstrument();

  const instruments = instrumentsQuery.data?.instruments ?? [];

  const busy =
    createWindow.isPending ||
    updateWindow.isPending ||
    createPoint.isPending ||
    updatePoint.isPending;

  const mutationError =
    createWindow.error ?? updateWindow.error ?? createPoint.error ?? updatePoint.error;

  const close = () => {
    setEditing(null);
    createWindow.reset();
    updateWindow.reset();
    createPoint.reset();
    updatePoint.reset();
  };

  // Short zone label, e.g. "London". Blank for the broker clock, which needs
  // no qualifier because it is the default frame everything else is shown in.
  const zoneLabel = (tz: string | null) =>
    tz ? (MARKER_ZONES.find((z) => z.id === tz)?.label ?? tz) : '';

  const nameFor = (ids: number[]) =>
    ids.length === 0
      ? t('settings.allPairs')
      : ids
          .map((id) => instruments.find((i) => i.id === id)?.symbol ?? `#${id}`)
          .join(', ');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{t('settings.title')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('settings.subtitle')}
        </p>
      </div>

      {/* ---------------- trading pairs ---------------- */}
      <section className="glass rounded-2xl p-4">
        <h2 className="flex items-center gap-2 text-sm font-medium">
          <GapIcon size={16} className="text-primary" />
          {t('settings.tradingPairs')}
        </h2>
        <p className="mt-1 text-xs text-muted-foreground">
          {instrumentsQuery.data?.hint ??
            'Pairs appear here once the MT5 exporter writes them.'}
        </p>

        <div className="mt-3 space-y-1.5">
          {instruments.length === 0 && !instrumentsQuery.isPending && (
            <p className="py-4 text-center text-sm text-muted-foreground">
              Nothing ingested yet.
            </p>
          )}

          {instruments.map((i) => (
            <div
              key={i.id}
              className="flex items-center gap-3 rounded-xl border border-border bg-surface/50 px-3 py-2"
            >
              <label className="flex cursor-pointer items-center gap-2">
                <input
                  type="checkbox"
                  checked={i.enabled}
                  onChange={(e) =>
                    updateInstrument.mutate({ id: i.id, enabled: e.target.checked })
                  }
                  className="size-4 accent-[var(--primary)]"
                />
                <PairIcon symbol={i.symbol} size={20} />
                <span className="tabular text-sm font-medium">{i.symbol}</span>
              </label>
              <span className="truncate text-xs text-muted-foreground">{i.description}</span>
              <span className="tabular ms-auto shrink-0 text-xs text-muted-foreground">
                {(i.bars ?? 0).toLocaleString()} bars
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* ---------------- alert sounds ---------------- */}
      <SoundSettings />

      {/* ---------------- time windows ---------------- */}
      <MarkerSection
        title={t('settings.timeWindows')}
        icon={<Clock size={16} variant="Bold" className="text-primary" />}
        description={t('settings.windowsDescription')}
        onAdd={() => setEditing({ kind: 'window' })}
        addLabel={t('settings.addWindow')}
        isLoading={windows.isPending}
        items={(windows.data?.windows ?? []).map((w) => ({
          id: w.id,
          color: w.color,
          name: w.name,
          enabled: w.enabled,
          detail: `${formatMinute(w.startMinute)}–${formatMinute(w.endMinute)}`,
          zone: zoneLabel(w.timezone),
          marks: importanceMarks(w.importance),
          pairs: nameFor(w.instrumentIds),
          onEdit: () => setEditing({ kind: 'window', value: w }),
          onDelete: () => deleteWindow.mutate(w.id),
        }))}
      />

      {/* ---------------- time points ---------------- */}
      <MarkerSection
        title={t('settings.timePoints')}
        icon={<Clock size={16} variant="Bold" className="text-primary" />}
        description={t('settings.pointsDescription')}
        onAdd={() => setEditing({ kind: 'point' })}
        addLabel={t('settings.addPoint')}
        isLoading={points.isPending}
        items={(points.data?.points ?? []).map((p) => ({
          id: p.id,
          color: p.color,
          name: p.name,
          enabled: p.enabled,
          detail: formatMinute(p.minute),
          zone: zoneLabel(p.timezone),
          pairs: nameFor(p.instrumentIds),
          onEdit: () => setEditing({ kind: 'point', value: p }),
          onDelete: () => deletePoint.mutate(p.id),
        }))}
      />

      <p className="tabular pt-2 text-center text-[10px] text-muted-foreground/70">
        {t('settings.build', { version: __APP_VERSION__ })}
      </p>

      {/* ---------------- editor ---------------- */}
      <Dialog.Root open={editing !== null} onOpenChange={(open) => !open && close()}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" />
          <Dialog.Content
            className={cn(
              'fixed top-1/2 start-1/2 z-50 max-h-[88vh] w-[min(30rem,92vw)] -translate-x-1/2 -translate-y-1/2',
              'overflow-y-auto glass rounded-2xl p-5 shadow-2xl',
              'scrollbar-slim',
            )}
          >
            <Dialog.Title className="mb-4 text-base font-semibold">
              {editing?.value ? 'Edit' : 'New'}{' '}
              {editing?.kind === 'point' ? 'time point' : 'time window'}
            </Dialog.Title>
            {/* Radix warns without a description; the forms are self-explanatory. */}
            <Dialog.Description className="sr-only">
              Configure when this marker appears and which pairs it applies to.
            </Dialog.Description>

            {editing?.kind === 'window' && (
              <WindowForm
                initial={editing.value}
                instruments={instruments}
                busy={busy}
                error={mutationError instanceof Error ? mutationError.message : null}
                onCancel={close}
                onSubmit={(input) => {
                  const done = { onSuccess: close };
                  if (editing.value) {
                    updateWindow.mutate({ id: editing.value.id, input }, done);
                  } else {
                    createWindow.mutate(input, done);
                  }
                }}
              />
            )}

            {editing?.kind === 'point' && (
              <PointForm
                initial={editing.value}
                instruments={instruments}
                busy={busy}
                error={mutationError instanceof Error ? mutationError.message : null}
                onCancel={close}
                onSubmit={(input) => {
                  const done = { onSuccess: close };
                  if (editing.value) {
                    updatePoint.mutate({ id: editing.value.id, input }, done);
                  } else {
                    createPoint.mutate(input, done);
                  }
                }}
              />
            )}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}

interface MarkerItem {
  id: number;
  color: string;
  name: string;
  enabled: boolean;
  detail: string;
  /** Timezone label, blank when the marker is on the broker clock. */
  zone: string;
  /** "!" / "!!" / "!!!" — windows only; points have no importance. */
  marks?: string;
  pairs: string;
  onEdit: () => void;
  onDelete: () => void;
}

function MarkerSection({
  title,
  icon,
  description,
  items,
  onAdd,
  addLabel,
  isLoading,
}: {
  title: string;
  icon: React.ReactNode;
  description: string;
  items: MarkerItem[];
  onAdd: () => void;
  addLabel: string;
  isLoading: boolean;
}) {
  const { t } = useI18n();

  return (
    <section className="glass rounded-2xl p-4">
      <div className="flex items-center gap-2">
        <h2 className="flex items-center gap-2 text-sm font-medium">
          {icon}
          {title}
        </h2>
        <Button size="sm" variant="secondary" className="ms-auto" onClick={onAdd}>
          <Add size={15} />
          {addLabel}
        </Button>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">{description}</p>

      <div className="mt-3 space-y-1.5">
        {isLoading && <p className="py-3 text-sm text-muted-foreground">{t('common.loading')}</p>}

        {!isLoading && items.length === 0 && (
          <p className="flex items-center justify-center gap-2 py-5 text-sm text-muted-foreground">
            <InfoCircle size={15} />
            None yet.
          </p>
        )}

        {items.map((item) => (
          <div
            key={item.id}
            className={cn(
              'flex items-center gap-3 rounded-xl border border-border bg-surface/50 px-3 py-2',
              !item.enabled && 'opacity-55',
            )}
          >
            <span
              className="size-3 shrink-0 rounded-sm"
              style={{ backgroundColor: item.color }}
              aria-hidden="true"
            />
            <div className="min-w-0">
              <div className="truncate text-sm font-medium">{item.name}</div>
              <div className="truncate text-xs text-muted-foreground">{item.pairs}</div>
            </div>
            {item.marks && (
              <span
                className="ms-auto shrink-0 text-sm font-bold tracking-tight text-primary"
                title={`Importance: ${item.marks}`}
              >
                {item.marks}
              </span>
            )}
            <span
              className={cn(
                'shrink-0 text-end text-xs text-muted-foreground',
                !item.marks && 'ms-auto',
              )}
            >
              <span className="tabular block">{item.detail}</span>
              {item.zone && <span className="block text-[10px] opacity-70">{item.zone}</span>}
            </span>
            <Button size="icon" variant="ghost" aria-label={t('common.edit')} onClick={item.onEdit}>
              <Edit2 size={15} />
            </Button>
            <Button size="icon" variant="ghost" aria-label={t('common.delete')} onClick={item.onDelete}>
              <Trash size={15} className="text-danger" />
            </Button>
          </div>
        ))}
      </div>
    </section>
  );
}
